import { useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
} from "@mui/material";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";

import PageHeader from "../../components/common/PageHeader";
import PageActions from "../../components/common/PageActions";
import FilterBar from "../../components/common/FilterBar";
import EmptyState from "../../components/common/EmptyState";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DataTableWrapper from "../../components/common/DataTableWrapper";
import PaginationBar from "../../components/common/PaginationBar";

import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useGlobalModals } from "../../contexts/GlobalModalsContext";

import {
  useListTransfersQuery,
  useSetTransferStatusMutation,
  useArchiveTransferMutation,
  useExportTransfersXlsxMutation,
} from "../../features/transfers/transfersApi";
import type { TransferStatus } from "../../features/transfers/types";

import { useClientSearch, useProviderSearch } from "../../hooks/useMasterdataSearch";
import AsyncAutocompleteWithQuickCreate from "../../components/common/AsyncAutocompleteWithQuickCreate";
import type { Client, Provider } from "../../features/masterdata/types";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { usePersistedListFilters } from "../../hooks/usePersistedListFilters";
import { getTodayString } from "../../utils/date";

function statusChip(status: TransferStatus) {
  const label =
    status === "planned" ? "Planned" :
    status === "in_progress" ? "In progress" :
    status === "completed" ? "Completed" : "Cancelled";
  return <Chip size="small" label={label} />;
}

export default function TransfersListPage() {
  const nav = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { showError, showConfirm } = useGlobalModals();

  type Ordering = "service_date" | "-service_date" | "start_time" | "-start_time" | "created_at" | "-created_at";
  const [filters, setFilters] = usePersistedListFilters("transfers", {
    dateFrom: getTodayString(),
    dateTo: getTodayString(),
    statusFilter: "" as TransferStatus | "",
    provider: "",
    client: "",
    search: "",
    ordering: "service_date" as Ordering,
  });
  const { dateFrom, dateTo, statusFilter, provider, client, search, ordering } = filters;
  const debouncedSearch = useDebouncedValue(search);

  const toggleSort = (field: "service_date" | "start_time" | "created_at") => {
    setFilters((prev) => ({
      ...prev,
      ordering: (prev.ordering === field ? `-${field}` : prev.ordering === `-${field}` ? field : field) as Ordering,
    }));
  };
  const sortIcon = (field: "service_date" | "start_time" | "created_at") => {
    if (ordering === field) return <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.25 }} />;
    if (ordering === `-${field}`) return <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.25 }} />;
    return <UnfoldMoreIcon sx={{ fontSize: 16, ml: 0.25, opacity: 0.5 }} />;
  };
  const sortAria = (field: "service_date" | "start_time" | "created_at"): "ascending" | "descending" | "none" =>
    ordering === field ? "ascending" : ordering === `-${field}` ? "descending" : "none";
  const [page, setPage] = useState(1);

   // actions (delete not supported by backend; confirm shows info message)
  const [setStatus, { isLoading: isSettingStatus }] = useSetTransferStatusMutation();
  const [archiveTransfer, { isLoading: isArchiving }] = useArchiveTransferMutation();
  const [exportTransfersXlsx, { isLoading: isExporting }] = useExportTransfersXlsxMutation();

  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, statusFilter, provider, client, debouncedSearch, ordering]);

  const [isProviderFilterOpen, setProviderFilterOpen] = useState(false);
  const [isClientFilterOpen, setClientFilterOpen] = useState(false);
  const providerSearch = useProviderSearch(provider || null, { menuOpen: isProviderFilterOpen });
  const clientSearch = useClientSearch(client || null, { menuOpen: isClientFilterOpen });
  const providerFilterOptions = useMemo(
    () => [{ id: "", name: "All" } as Provider, ...providerSearch.options],
    [providerSearch.options]
  );
  const clientFilterOptions = useMemo(
    () => [{ id: "", name: "All" } as Client, ...clientSearch.options],
    [clientSearch.options]
  );

  const { data, isLoading, isFetching, error, refetch } = useListTransfersQuery({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    status: statusFilter || undefined,
    provider: provider || undefined,
    client: client || undefined,
    search: debouncedSearch,
    ordering,
    page,
  });

  const rows = data?.results ?? [];
  const count = data?.count ?? 0;
  const canPrev = !!data?.previous && page > 1;
  const canNext = !!data?.next;

  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteName, setDeleteName] = useState<string>("");

  const onConfirmDelete = async () => {
    if (!deleteId) return;
    setOpenDelete(false);
    setDeleteId(null);
    setDeleteName("");
    enqueueSnackbar("Deleting transfers is not supported. Use status or archive instead.", { variant: "info" });
  };

  const onQuickStatus = async (id: string, next: TransferStatus) => {
    try {
      await setStatus({ id, status: next }).unwrap();
      enqueueSnackbar(`Status updated to ${next}`, { variant: "success" });
    } catch (e: any) {
      enqueueSnackbar(e?.data?.detail || "Failed to update status", { variant: "error" });
    }
  };

  const onArchive = async (id: string) => {
    const confirmed = await showConfirm({
      title: "Archive transfer?",
      message: "This transfer will be archived and hidden from the default list.",
      confirmText: "Archive",
      cancelText: "Cancel",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await archiveTransfer({ id }).unwrap();
      enqueueSnackbar("Transfer archived", { variant: "success" });
    } catch (e: any) {
      const msg = e?.data?.detail || "Failed to archive transfer";
      enqueueSnackbar(msg, { variant: "error" });
      showError({ title: "Archive failed", message: typeof msg === "string" ? msg : "Could not archive transfer.", details: e?.data ? JSON.stringify(e.data, null, 2) : undefined });
    }
  };

  const onStartExport = () => {
    exportTransfersXlsx({
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      status: statusFilter || undefined,
      provider: provider || undefined,
      client: client || undefined,
      search: debouncedSearch || undefined,
      ordering,
    })
      .unwrap()
      .then(() => enqueueSnackbar("Export downloaded", { variant: "success" }))
      .catch((e: any) =>
        enqueueSnackbar(e?.data?.detail || "Export failed", { variant: "error" })
      );
  };

  const showEmpty = !isLoading && !error && rows.length === 0;

  return (
    <Box>
      <PageHeader
        title="Transfers"
        subtitle="Plan and track daily routes"
        actions={
          <PageActions
            onStartExport={onStartExport}
            exportDisabled={isExporting}
          >
            <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={() => nav("/app/transfers/new")}>
              New Transfer
            </Button>
          </PageActions>
        }
      />

      <FilterBar loading={isLoading || isFetching}>
        <TextField
          label="Date from"
          type="date"
          value={dateFrom}
          onChange={(e) => setFilters({ dateFrom: e.target.value })}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ minWidth: { xs: 0, sm: 160 }, width: { xs: "100%", sm: "auto" } }}
        />
        <TextField
          label="Date to"
          type="date"
          value={dateTo}
          onChange={(e) => setFilters({ dateTo: e.target.value })}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ minWidth: { xs: 0, sm: 160 }, width: { xs: "100%", sm: "auto" } }}
        />
        <TextField
          label="Status"
          select
          value={statusFilter}
          onChange={(e) => setFilters({ statusFilter: e.target.value as TransferStatus | "" })}
          size="small"
          sx={{ minWidth: { xs: 0, sm: 150 }, width: { xs: "100%", sm: "auto" } }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="planned">Planned</MenuItem>
          <MenuItem value="in_progress">In progress</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>
        <Box sx={{ minWidth: { xs: 0, sm: 180 }, width: { xs: "100%", sm: "auto" } }}>
          <AsyncAutocompleteWithQuickCreate<Provider>
            label="Provider"
            value={provider}
            onChange={(id) => setFilters({ provider: id ?? "" })}
            options={providerFilterOptions}
            getOptionLabel={(p) => p.name}
            showCreate={false}
            loading={providerSearch.loading}
            onInputChange={(_, v) => providerSearch.setSearchTerm(v)}
            filterOptions={(opts) => opts}
            onOpen={() => setProviderFilterOpen(true)}
            onClose={() => setProviderFilterOpen(false)}
            placeholder="Type to search…"
          />
        </Box>
        <Box sx={{ minWidth: { xs: 0, sm: 180 }, width: { xs: "100%", sm: "auto" } }}>
          <AsyncAutocompleteWithQuickCreate<Client>
            label="Client"
            value={client}
            onChange={(id) => setFilters({ client: id ?? "" })}
            options={clientFilterOptions}
            getOptionLabel={(c) => c.name}
            showCreate={false}
            loading={clientSearch.loading}
            onInputChange={(_, v) => clientSearch.setSearchTerm(v)}
            filterOptions={(opts) => opts}
            onOpen={() => setClientFilterOpen(true)}
            onClose={() => setClientFilterOpen(false)}
            placeholder="Type to search…"
          />
        </Box>
        <TextField
          label="Search (description, notes)"
          value={search}
          onChange={(e) => setFilters({ search: e.target.value })}
          size="small"
          placeholder="Search…"
          sx={{ minWidth: 0, flex: 1, width: { xs: "100%", sm: "auto" } }}
        />
      </FilterBar>

      {showEmpty ? (
        <EmptyState
          title="No transfers yet"
          description="Create your first transfer."
          actionLabel="Create transfer"
          onAction={() => nav("/app/transfers/new")}
        />
      ) : (
        <>
          <DataTableWrapper
            loading={isLoading}
            error={!!error}
            errorMessage="Failed to load transfers."
            onRetry={() => refetch()}
            minWidth={720}
          >
            <Table sx={{ minWidth: 720 }} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell aria-sort={sortAria("service_date")}>
                    <Box
                      component="span"
                      onClick={() => toggleSort("service_date")}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "pointer",
                        fontWeight: 600,
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      Date{sortIcon("service_date")}
                    </Box>
                  </TableCell>
                  <TableCell aria-sort={sortAria("start_time")}>
                    <Box
                      component="span"
                      onClick={() => toggleSort("start_time")}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "pointer",
                        fontWeight: 600,
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      Time{sortIcon("start_time")}
                    </Box>
                  </TableCell>
                  <TableCell>Description / Route</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Cost (provider)</TableCell>
                  <TableCell align="right">Charge (client)</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.service_date}</TableCell>
                    <TableCell>{r.start_time}</TableCell>
                    <TableCell>
                      <Box sx={{ fontWeight: 600 }}>{r.description || "—"}</Box>
                    </TableCell>
                    <TableCell>{r.provider_name ?? "—"}</TableCell>
                    <TableCell>{r.client_name ?? "—"}</TableCell>
                    <TableCell>{statusChip(r.status)}</TableCell>
                    <TableCell align="right">
                      {r.price_to_provider != null ? `€${r.price_to_provider}` : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {r.price_to_client != null ? `€${r.price_to_client}` : "—"}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Mark completed">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onQuickStatus(r.id, "completed")}
                            disabled={isSettingStatus || r.status === "completed"}
                            aria-label="Mark completed"
                          >
                            <CheckCircleOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Cancel transfer">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onQuickStatus(r.id, "cancelled")}
                            disabled={isSettingStatus || r.status === "cancelled"}
                            aria-label="Cancel transfer"
                          >
                            <CancelOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => nav(`/app/transfers/${r.id}/edit`)}
                          aria-label="Edit transfer"
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!r.is_archived && (
                        <Tooltip title="Archive">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => onArchive(r.id)}
                              disabled={isArchiving}
                              aria-label="Archive transfer"
                            >
                              <ArchiveOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DataTableWrapper>

          <PaginationBar
            count={count}
            page={page}
            hasPrev={canPrev}
            hasNext={!!canNext}
            disabled={isFetching}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />
        </>
      )}

      <ConfirmDialog
        open={openDelete}
        title="Delete transfer?"
        description={deleteId ? `This will delete "${deleteName}".` : undefined}
        confirmText="Delete"
        severity="destructive"
        loading={false}
        onClose={() => {
          setOpenDelete(false);
          setDeleteId(null);
        }}
        onConfirm={onConfirmDelete}
      />
    </Box>
  );
}
